import { SparePartModel } from '../models/spare-part-model/spare-part-model';
import {
  SPARE_PART_DAMAGED_NO,
  SPARE_PART_DAMAGED_PENDIENTE_APROBACION,
  SPARE_PART_DAMAGED_SI_ACEPTADO,
  SPARE_PART_DAMAGED_SI_CONSULTADO_TALLER,
  SPARE_PART_DAMAGED_SI_NO_ACEPTADO,
  SPARE_PART_DAMAGED_STATES,
  SparePartDamagedState
} from '../constants/spare-part-damaged-states';

type NeedReviewCarrier = {
  needReview?: boolean | string | number;
  NeedReview?: boolean | string | number;
  sparePartName?: string;
  productCode?: string;
  [key: string]: unknown;
};

/** Lee needReview desde distintas formas que puede devolver la API o el formulario. */
export function readNeedReviewFlag(sp: NeedReviewCarrier | null | undefined): boolean {
  if (!sp) {
    return false;
  }

  const raw = sp.needReview ?? sp.NeedReview;
  if (raw === true || raw === 1) {
    return true;
  }
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí' || normalized === 'yes';
  }
  return false;
}

/** true cuando el repuesto está marcado como que necesita revisión. */
export function isNeedReviewBlocking(sp: NeedReviewCarrier | null | undefined): boolean {
  return readNeedReviewFlag(sp);
}

export function getSparePartsBlockedByNeedReview<T extends NeedReviewCarrier>(
  parts: T[] | null | undefined
): T[] {
  return (parts || []).filter(sp => isNeedReviewBlocking(sp));
}

export function normalizeNeedReviewOnSparePart<T extends NeedReviewCarrier>(sp: T): T {
  return {
    ...sp,
    needReview: readNeedReviewFlag(sp)
  };
}

export function formatNeedReviewPackagingBlockMessage(parts: NeedReviewCarrier[]): string {
  if (!parts.length) {
    return '';
  }
  const details = parts
    .map(sp => `${sp.sparePartName || 'Sin nombre'} (${sp.productCode || 'sin código'})`)
    .join(', ');
  return `No se puede confirmar el empaquetado. Los siguientes repuestos necesitan revisión: ${details}`;
}

type IsDamagedCarrier = {
  isDamaged?: string | boolean | null;
  IsDamaged?: string | boolean | null;
  sparePartName?: string;
  productCode?: string;
  [key: string]: unknown;
};

/** Normaliza isDamaged a uno de los estados canónicos. */
export function normalizeIsDamaged(value: string | boolean | null | undefined): SparePartDamagedState {
  if (value === null || value === undefined) {
    return SPARE_PART_DAMAGED_NO;
  }

  if (typeof value === 'boolean') {
    return value ? SPARE_PART_DAMAGED_PENDIENTE_APROBACION : SPARE_PART_DAMAGED_NO;
  }

  const trimmed = `${value}`.trim();
  if (!trimmed) {
    return SPARE_PART_DAMAGED_NO;
  }

  const canonical = SPARE_PART_DAMAGED_STATES.find(
    state => state.toLowerCase() === trimmed.toLowerCase()
  );
  if (canonical) {
    return canonical;
  }

  const lower = trimmed.toLowerCase();
  if (lower === 'pendiente aprobacion') {
    // Legacy value before adding "Aldana"
    return SPARE_PART_DAMAGED_PENDIENTE_APROBACION;
  }
  if (lower === 'si, consultado taller' || lower === 'sí, consultado taller') {
    return SPARE_PART_DAMAGED_SI_CONSULTADO_TALLER;
  }
  if (lower === 'true' || lower === '1' || lower === 'si' || lower === 'sí' || lower === 'yes') {
    return SPARE_PART_DAMAGED_PENDIENTE_APROBACION;
  }
  if (lower === 'false' || lower === '0' || lower === 'no') {
    return SPARE_PART_DAMAGED_NO;
  }

  return SPARE_PART_DAMAGED_NO;
}

export function readIsDamagedState(sp: IsDamagedCarrier | null | undefined): SparePartDamagedState {
  if (!sp) {
    return SPARE_PART_DAMAGED_NO;
  }
  return normalizeIsDamaged(sp.isDamaged ?? sp.IsDamaged ?? null);
}

/** true cuando el daño impide confirmar empaquetado. */
export function isDamagedBlocking(sp: IsDamagedCarrier | null | undefined): boolean {
  const state = readIsDamagedState(sp);
  return state === SPARE_PART_DAMAGED_SI_NO_ACEPTADO
    || state === SPARE_PART_DAMAGED_SI_CONSULTADO_TALLER
    || state === SPARE_PART_DAMAGED_PENDIENTE_APROBACION;
}

export function getSparePartsBlockedByIsDamaged<T extends IsDamagedCarrier>(
  parts: T[] | null | undefined
): T[] {
  return (parts || []).filter(sp => isDamagedBlocking(sp));
}

export function normalizeIsDamagedOnSparePart<T extends IsDamagedCarrier>(sp: T): T {
  return {
    ...sp,
    isDamaged: readIsDamagedState(sp)
  };
}

export function formatIsDamagedPackagingBlockMessage(parts: IsDamagedCarrier[]): string {
  if (!parts.length) {
    return '';
  }
  const details = parts
    .map(sp => `${sp.sparePartName || 'Sin nombre'} (${sp.productCode || 'sin código'}) — ${readIsDamagedState(sp)}`)
    .join(', ');
  return `No se puede confirmar el empaquetado. Los siguientes repuestos tienen daño no aprobado: ${details}`;
}

export function isPackagingConfirmationBlocked(
  sp: (NeedReviewCarrier & IsDamagedCarrier) | null | undefined
): boolean {
  return isNeedReviewBlocking(sp) || isDamagedBlocking(sp);
}

export function getSparePartsBlockedForPackagingConfirmation<T extends NeedReviewCarrier & IsDamagedCarrier>(
  parts: T[] | null | undefined
): T[] {
  return (parts || []).filter(sp => isPackagingConfirmationBlocked(sp));
}

export function normalizeSparePartForPackagingConfirmation<T extends NeedReviewCarrier & IsDamagedCarrier>(sp: T): T {
  return normalizeIsDamagedOnSparePart(normalizeNeedReviewOnSparePart(sp));
}

export function formatPackagingConfirmationBlockMessage(parts: Array<NeedReviewCarrier & IsDamagedCarrier>): string {
  const needReviewParts = getSparePartsBlockedByNeedReview(parts);
  const damagedParts = getSparePartsBlockedByIsDamaged(parts);
  const messages: string[] = [];

  if (needReviewParts.length > 0) {
    messages.push(formatNeedReviewPackagingBlockMessage(needReviewParts));
  }
  if (damagedParts.length > 0) {
    messages.push(formatIsDamagedPackagingBlockMessage(damagedParts));
  }

  return messages.join(' ');
}

/** Repuesto que ya pasó por el flujo de empaquetado, devolución a proveedor o pendiente a solución. */
export function isSparePartPackaged(sp: SparePartModel | null | undefined): boolean {
  if (!sp) {
    return false;
  }
  const status = (sp.productStatus || '').trim();
  const shipping = (sp.shippingStatus || '').trim();
  return (
    status.startsWith('EMPAQUETADO-') ||
    status === 'EXPORTADO' ||
    status === 'DEVOLUCION-PROVEEDOR' ||
    status === 'PENDIENTE A SOLUCIÓN' ||
    shipping === 'ENVIADO'
  );
}

export function getUnpackagedSpareParts(parts: SparePartModel[] | null | undefined): SparePartModel[] {
  return (parts || []).filter(sp => !isSparePartPackaged(sp));
}

export function getPackagedSpareParts(parts: SparePartModel[] | null | undefined): SparePartModel[] {
  return (parts || []).filter(sp => isSparePartPackaged(sp));
}

export function canGeneratePackagesForOrder(parts: SparePartModel[] | null | undefined): boolean {
  return getUnpackagedSpareParts(parts).length > 0;
}

/** Paquete ya confirmado en empaquetado: no admite nuevos repuestos. */
export function isPackageClosedForAssignment(
  packageNumber: string,
  orderSpareParts: SparePartModel[] | null | undefined
): boolean {
  const normalized = (packageNumber || '').trim();
  if (!normalized) {
    return false;
  }

  const partsInPackage = (orderSpareParts || []).filter(
    sp => (sp.packageNumber || '').trim() === normalized
  );

  return partsInPackage.some(sp => isSparePartPackaged(sp));
}

type PackageOrPrintedLabelCarrier = {
  packageNumber?: string | null;
  PackageNumber?: string | null;
  labelPrintedAt?: string | Date | null;
  LabelPrintedAt?: string | Date | null;
  labelPrintCount?: number | string | null;
  LabelPrintCount?: number | string | null;
};

export const SPARE_PART_DELETE_BLOCKED_MESSAGE =
  'No se puede eliminar el repuesto porque tiene paquete o etiqueta impresa.';

export const ORDER_DELETE_BLOCKED_MESSAGE =
  'No se puede eliminar la orden porque tiene repuestos con paquete o etiqueta impresa.';

/** true si el repuesto tiene número de paquete, fecha de etiqueta o contador de impresión. */
export function sparePartHasPackageOrPrintedLabel(
  sparePart: PackageOrPrintedLabelCarrier | null | undefined
): boolean {
  if (!sparePart) {
    return false;
  }

  const packageNumber = sparePart.packageNumber ?? sparePart.PackageNumber;
  if (typeof packageNumber === 'string' && packageNumber.trim() !== '') {
    return true;
  }

  const labelPrintedAt = sparePart.labelPrintedAt ?? sparePart.LabelPrintedAt;
  if (labelPrintedAt != null && labelPrintedAt !== '') {
    return true;
  }

  const rawCount = sparePart.labelPrintCount ?? sparePart.LabelPrintCount;
  const labelPrintCount = typeof rawCount === 'string' ? Number(rawCount) : rawCount;
  return typeof labelPrintCount === 'number' && !Number.isNaN(labelPrintCount) && labelPrintCount > 0;
}

export function readApiErrorMessage(error: unknown, fallback: string): string {
  const body = (error as { error?: unknown } | null | undefined)?.error;
  if (typeof body === 'string' && body.trim() !== '') {
    return body;
  }

  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
  }

  return fallback;
}
